import { useTranslation } from '@sib/i18n';
import { FileStatus } from '@sib/types';
import { TestIds } from '@sib/types/test-ids';
import { Button, Loader, Tooltip } from '@sib/ui';
import { Arrow } from '@sib/ui/icons';
import { useStorageConnectivityCheck } from '@spendbrain/common/hooks';
import { useFileManagerFileUploadStore } from '@spendbrain/stores/fileUploadStore';

interface UploadFileButtonProps {
  onClick: () => void;
}

export const UploadFileButton = ({ onClick }: UploadFileButtonProps) => {
  const { t } = useTranslation();
  const { storageConnectionFailed } = useStorageConnectivityCheck();
  const files = useFileManagerFileUploadStore(store => store.files);
  const uploadingFilesCount = files.filter(
    fileItem => fileItem.status === FileStatus.Uploading
  ).length;
  const isUploading = uploadingFilesCount > 0;
  const buttonLabel = isUploading
    ? t('fileManager.uploadingFiles', { count: uploadingFilesCount })
    : t('fileManager.uploadFiles');

  return (
    <Tooltip
      content={
        storageConnectionFailed
          ? t('failedStorageConnection')
          : t('dragAndDrop')
      }
      TooltipTriggerProps={{ asChild: !storageConnectionFailed }}
      {...(storageConnectionFailed && {
        TooltipContentProps: {
          side: 'top',
          variant: 'secondary',
          className: 'text-sm max-w-[320px]',
        },
      })}
    >
      <Button
        disabled={storageConnectionFailed}
        onClick={onClick}
        className="p-0.5 disabled:opacity-60 sm:p-[6px]"
        variant="outline"
        data-test-id={TestIds.FileUploadButton}
      >
        <div className="border-primary/50 flex items-center gap-2 rounded-sm border border-dashed px-1.5 py-0.5 sm:px-3">
          {isUploading ? (
            <Loader className="text-primary h-5" />
          ) : (
            <Arrow className="text-primary h-5" />
          )}
          <p className="text-primary hidden max-w-[140px] truncate leading-4 md:block">
            {buttonLabel}
          </p>
        </div>
      </Button>
    </Tooltip>
  );
};
